// src/utils/campaignUtils.js
// Utilities for building enemy decks from JSON and granting rewards.
// Requires getCardStats(instance, baseCard) from ../utilities

import { getCardStats } from '../utilities';

/**
 * Convert a JSON deck definition into PlayingCard-like objects for UI/combat.
 * deck: [{ cardId, level, evolutionStage }, ...]
 * globalCards: map of cardId -> base card data (from UserContext.globalCards)
 */
export function buildEnemyPlayingCards(deck = [], globalCards = {}) {
    return deck.slice(0, 5).map((entry) => {
        const base =
            globalCards[entry.cardId] || { id: entry.cardId, attack: 0, defense: 0, rarity: 'Common' };
        const instance = {
            cardId: entry.cardId,
            level: Number(entry.level || 1),
            evolutionStage: Math.max(1, Number(entry.evolutionStage || 1)),
            evolutionBonus: 0,
        };
        const stats = getCardStats(instance, base);
        return {
            id: base.id || entry.cardId,
            cardId: entry.cardId,
            name: base.name || entry.cardId,
            rarity: base.rarity,
            classification: base.classification,
            attack: stats.attack || base.attack || 0,
            defense: stats.defense || base.defense || 0,
            ability: base.ability,
            abilityDescription: base.abilityDescription,
        };
    });
}

/**
 * Apply a stage reward to the user via UserContext helpers.
 * reward example: { coins: 25, drawTickets: { basic: 1 }, items: { soulDust: 5 }, cards: [{ id: 'wolf' }] }
 * ctx should be your UserContext value (addCoins, addCardsToOwned, setUserData, persistUserData, userData)
 */
export async function grantStageReward(reward, ctx) {
    if (!reward || !ctx) return;
    const { addCoins, addCardsToOwned, setUserData, persistUserData, userData } = ctx;

    // Coins
    if (Number.isFinite(reward.coins) && reward.coins > 0) {
        addCoins && addCoins(Math.floor(reward.coins));
    }

    // Draw tickets
    if (reward.drawTickets && userData) {
        const next = {
            ...(userData.drawTickets || {}),
            ...Object.fromEntries(
                Object.entries(reward.drawTickets).map(([k, v]) => [
                    k,
                    (userData.drawTickets?.[k] || 0) + (v || 0),
                ])
            ),
        };
        setUserData && setUserData((prev) => ({ ...prev, drawTickets: next }));
        persistUserData && (await persistUserData({ drawTickets: next }));
    }

    // Items / consumables
    if (reward.items && userData) {
        const next = {
            ...(userData.consumables || {}),
            ...Object.fromEntries(
                Object.entries(reward.items).map(([k, v]) => [
                    k,
                    (userData.consumables?.[k] || 0) + (v || 0),
                ])
            ),
        };
        setUserData && setUserData((prev) => ({ ...prev, consumables: next }));
        persistUserData && (await persistUserData({ consumables: next }));
    }

    // Fixed card rewards
    if (Array.isArray(reward.cards) && reward.cards.length > 0) {
        addCardsToOwned && addCardsToOwned(reward.cards);
    }
}
